export const EducationType = Object.freeze({
  HIGH_SCHOOL: "High School",
  BACHELORS_DEGREE: "Bachelor",
  MASTERS_DEGREE: "Master",
  CERTIFICATION: "Certification",
});

export const InsuranceType = Object.freeze({
  PUBLIC_LIABILITY_PROFESSIONAL_INDEMNITY: "Public Liability / Professional Indemnity",
  WORKER_COMPENSATION: "Worker Compensation",
  OTHER: "Other",
});

export const PaymentType = Object.freeze({
  HOURLY: "Hourly",
  HALF_DAY: "Half Day",
  FULL_DAY: "Full Day",
  FIXED: "Fixed",
  FTH: "FTH",
  CUSTOM_HOURS: "Fixed Hours",
});

export const PaymentTypeTooltip = Object.freeze({
  HOURLY:
    "Paid per hour with a guaranteed minimum number of hours. Any time worked beyond the minimum is billed at the additional hourly rate.",
  HALF_DAY:
    "A flat rate covering up to 4 hours on site. Any time worked beyond 4 hours is billed at the additional hourly rate.",
  FULL_DAY:
    "A flat rate covering up to 8 hours on site. Any time worked beyond 8 hours is billed at the additional hourly rate.",
  FIXED:
    "A single agreed amount for the whole job, regardless of how long it takes. The approximate hours are used for scheduling only.",
  FTH: "A flat rate covering the first two hours on site. Any time worked beyond 2 hours is billed at the additional hourly rate.",
  CUSTOM_HOURS:
    "A flat rate covering a fixed block of hours that you define. Any time worked beyond that block is billed at the additional hourly rate.",
});

export const TeamTaskStatusType = Object.freeze({
  TODO: "TODO",
  INPROCESS: "INPROCESS",
  COMPLETED: "COMPLETED",
  ON_HOLD: "ON_HOLD",
});

export const BankAccountType = Object.freeze({
  CHECKING: "Checking",
  SAVINGS: "Saving",
  CURRENT: "Current",
});

export const UserType = Object.freeze({
  ADMIN: "ADMIN",
  CLIENT: "CLIENT",
  CUSTOMER: "CUSTOMER",
  FREELANCER: "FREELANCER",
});

export const WorkOrderStatusType = Object.freeze({
  DRAFT: "DRAFT",
  PENDING_APPROVAL: "PENDING_APPROVAL",
  PUBLISHED: "PUBLISHED",
  ROUTED: "ROUTED",
  ASSIGNED: "ASSIGNED",
  START_TIME_SET: "START_TIME_SET",
  INPROGRESS: "INPROGRESS",
  CONFIRM: "CONFIRM",
  INCOMPLETE: "INCOMPLETE",
  CANCELLED: "CANCELLED",
  AT_RISK: "AT_RISK",
  DELAYED: "DELAYED",
  ON_HOLD: "ON_HOLD",
  ON_THE_WAY: "ON_THE_WAY",
  ON_MY_WAY: "ON_MY_WAY",
  RUNNING_LATE: "RUNNING_LATE",
  CHECK_IN: "CHECK_IN",
  CHECK_OUT: "CHECK_OUT",
  WORKDONE: "WORKDONE",
  VERIFIED: "VERIFIED",
  CANCELLED_PAYABLE: "CANCELLED_PAYABLE",
  APPROVED: "APPROVED",
  INVOICED: "INVOICED",
  PAID: "PAID",
  DISPUTED: "DISPUTED",
  PO_PENDING: "PO_PENDING",
  // Sub statuses of a Submitted (PENDING_APPROVAL) ticket while the client's
  // counter offer to the customer is being negotiated. One value for both sides;
  // WorkOrderStatus labels PENDING by role (client "sent", customer "received").
  COUNTER_OFFER_PENDING: "COUNTER_OFFER_PENDING",
  COUNTER_OFFER_ACCEPTED: "COUNTER_OFFER_ACCEPTED",
  COUNTER_OFFER_DECLINED: "COUNTER_OFFER_DECLINED",
});

export const COUNTER_OFFER_SUB_STATUSES = Object.freeze([
  WorkOrderStatusType.COUNTER_OFFER_PENDING,
  WorkOrderStatusType.COUNTER_OFFER_ACCEPTED,
  WorkOrderStatusType.COUNTER_OFFER_DECLINED,
]);

/**
 * The only statuses a CUSTOMER may edit a work order in — Draft, Submitted and
 * Accepted, as those tabs are labelled on their board. Once an engineer is
 * assigned the work is under way and changes are the client's to make.
 *
 * Clients are not bound by this; their own rule lives in DetailsTab.
 */
export const CUSTOMER_EDITABLE_STATUSES = Object.freeze([
  WorkOrderStatusType.DRAFT,
  WorkOrderStatusType.PENDING_APPROVAL,
  WorkOrderStatusType.PUBLISHED,
]);

export const CUSTOMER_PO_ONLY_EDITABLE_STATUSES = Object.freeze([
  WorkOrderStatusType.PUBLISHED,
  WorkOrderStatusType.ROUTED,
  WorkOrderStatusType.ASSIGNED,
  WorkOrderStatusType.START_TIME_SET,
  WorkOrderStatusType.INPROGRESS,
  WorkOrderStatusType.WORKDONE,
  WorkOrderStatusType.VERIFIED,
  WorkOrderStatusType.APPROVED,
]);

export const InvoiceStatusType = Object.freeze({
  GENERATED: "GENERATED",
  VERIFIED: "VERIFIED",
  UNPAID: "UNPAID",
  OVERDUE: "OVERDUE",
  PAID: "PAID",
});

export const InvoiceStatusLabel = Object.freeze({
  GENERATED: "Generated",
  VERIFIED: "Verified",
  UNPAID: "Unpaid",
  OVERDUE: "Overdue",
  PAID: "Paid",
});

/**
 * Customer-facing wording for invoice statuses. A customer sees the stage the
 * invoice has reached from their side, which is not the internal label: an
 * internally VERIFIED invoice is the one that has reached them ("Received"), and
 * an UNPAID one is the one that has been approved and issued. Use this anywhere
 * a CUSTOMER account is the reader; InvoiceStatusLabel stays the internal view.
 */
export const CustomerInvoiceStatusLabel = Object.freeze({
  VERIFIED: "Received",
  UNPAID: "Approved",
  OVERDUE: "Overdue",
  PAID: "Paid",
});

export const IncompleteWorkOrderReasonType = Object.freeze({
  MISSING_INCORRECT_DELIVERABLES: "Missing/Incorrect Deliverables",
  TIME_PAY_ICORRECT: "Time/Pay Incorrect",
  MISSING_SIGN_OFF_SHEET: "Missing Sign-Off Sheet",
  REQUIRED_PARTS_NOT_RETURNED: "Required Parts Not Returned",
  REVISIT_REQUESTED: "Revisit Requested",
});

export const CurrencySymbol = Object.freeze({
  USD: "USD",
  EUR: "EUR",
  AUD: "AUD",
  GBP: "GBP",
  NZD: "NZD",
  AED: "AED",
  AFN: "AFN",
  ALL: "ALL",
  AMD: "AMD",
  ANG: "ANG",
  AOA: "AOA",
  ARS: "ARS",
  AWG: "AWG",
  AZN: "AZN",
  BAM: "BAM",
  BBD: "BBD",
  BDT: "BDT",
  BGN: "BGN",
  BHD: "BHD",
  BIF: "BIF",
  BMD: "BMD",
  BND: "BND",
  BOB: "BOB",
  BRL: "BRL",
  BSD: "BSD",
  BTN: "BTN",
  BWP: "BWP",
  BYR: "BYR",
  BZD: "BZD",
  CAD: "CAD",
  CDF: "CDF",
  CFA: "CFA",
  CHF: "CHF",
  CLP: "CLP",
  CNY: "CNY",
  COP: "COP",
  CRC: "CRC",
  CUP: "CUP",
  CVE: "CVE",
  CZK: "CZK",
  DJF: "DJF",
  DKK: "DKK",
  DOP: "DOP",
  DZD: "DZD",
  ECS: "ECS",
  EGP: "EGP",
  ERN: "ERN",
  ETB: "ETB",
  GTQ: "GTQ",
  FJD: "FJD",
  FKP: "FKP",
  GEL: "GEL",
  GHS: "GHS",
  GIP: "GIP",
  GMD: "GMD",
  GNF: "GNF",
  GYD: "GYD",
  HKD: "HKD",
  HNL: "HNL",
  HTG: "HTG",
  HUF: "HUF",
  IDR: "IDR",
  ILS: "ILS",
  INR: "INR",
  IQD: "IQD",
  IRR: "IRR",
  ISK: "ISK",
  JMD: "JMD",
  JOD: "JOD",
  JPY: "JPY",
  KES: "KES",
  KGS: "KGS",
  KHR: "KHR",
  KMF: "KMF",
  KPW: "KPW",
  KRW: "KRW",
  KWD: "KWD",
  KYD: "KYD",
  KZT: "KZT",
  LAK: "LAK",
  LBP: "LBP",
  LKR: "LKR",
  LRD: "LRD",
  LSL: "LSL",
  LYD: "LYD",
  MAD: "MAD",
  MDK: "MKD",
  MGF: "MGF",
  MMK: "MMR",
  MNT: "MNT",
  MOP: "MOP",
  MVR: "MVR",
  MWK: "MWK",
  MXN: "MXN",
  MYR: "MYR",
  MZN: "MZN",
  NAD: "NAD",
  NGN: "NGN",
  NOK: "NOK",
  NPR: "NPR",
  OMR: "OMR",
  PAB: "PAB",
  PEN: "PEN",
  PGK: "PGK",
  PHP: "PHP",
  PKR: "PKR",
  PLN: "PLN",
  PYG: "PYG",
  QAR: "QAR",
  QTQ: "QTQ",
  RON: "RON",
  RSD: "RSD",
  RUB: "RUB",
  RWF: "RWF",
  SAR: "SAR",
  SBD: "SBD",
  SCR: "SCR",
  SDG: "SDG",
  SEK: "SEK",
  SGD: "SGD",
  SHP: "SHP",
  SLL: "SLL",
  SOS: "SOS",
  SRD: "SRD",
  SSP: "SSP",
  STD: "STD",
  SVC: "SVC",
  SYP: "SYP",
  SZL: "SZL",
  THB: "THB",
  TJS: "TJS",
  TMT: "TMT",
  TND: "TND",
  TOP: "TOP",
  TRY: "TRY",
  TTD: "TTD",
  TZS: "TZS",
  UAH: "UAH",
  UGX: "UGX",
  UYU: "UYU",
  UZS: "UZS",
  VEF: "VEF",
  VND: "VND",
  VUV: "VUV",
  WST: "WST",
  XAF: "XAF",
  XCD: "XCD",
  XOF: "XOF",
  XPF: "XPF",
  YER: "YER",
  ZAR: "ZAR",
  ZMW: "ZMW",
  ZWD: "ZWD",
});

export const UserProfileStatus = Object.freeze({
  PENDING: "PENDING",
  SUBMITTED: "SUBMITTED",
  ACTIVATED: "ACTIVATED",
  DEACTIVATED: "DEACTIVATED",
  REJECTED: "REJECTED",
  APPROVED: "APPROVED",
  UNAPPROVE: "UNAPPROVE",
});

export const ModuleType = Object.freeze({
  WORK_ORDERS: "WORK_ORDERS",
  CUSTOMER_WORK_ORDERS: "CUSTOMER_WORK_ORDERS",
  CLIENT_WORK_ORDERS: "CLIENT_WORK_ORDERS",
  ACTION_APPROVE_WORK_ORDER: "ACTION_APPROVE_WORK_ORDER",
  ACTION_VERIFY_WORK_ORDER: "ACTION_VERIFY_WORK_ORDER",
  ACTION_REVIEW_WORK_ORDER: "ACTION_REVIEW_WORK_ORDER",
  ACTION_CANCEL_WORK_ORDER: "ACTION_CANCEL_WORK_ORDER",
  COMPANIES: "COMPANIES",
  ADMIN_NEW_USERS_MANAGEMENT: "ADMIN_NEW_USERS_MANAGEMENT",
  USER_PERMISSIONS: "USER_PERMISSIONS",
  WORK_EXPERIENCE: "WORK_EXPERIENCE",
  QUALIFICATIONS: "QUALIFICATIONS",
  USERS: "USERS",
  JOBS: "JOBS",
  MANAGE: "MANAGE",
  CLIENT_CUSTOMER: "CLIENT_CUSTOMER",
  CLIENT_PROJECT: "CLIENT_PROJECT",
  INVOICE: "INVOICE",
  CUSTOMER_INVOICE: "CUSTOMER_INVOICE",
  ACTION_VERIFY_CUSTOMER_INVOICE: "ACTION_VERIFY_CUSTOMER_INVOICE",
  ACTION_SEND_CUSTOMER_INVOICE: "ACTION_SEND_CUSTOMER_INVOICE",
  ACTION_PAID_CUSTOMER_INVOICE: "ACTION_PAID_CUSTOMER_INVOICE",
  ENGINEER_INVOICE: "ENGINEER_INVOICE",
  ACTION_VERIFY_ENGINEER_INVOICE: "ACTION_VERIFY_ENGINEER_INVOICE",
  ACTION_SEND_ENGINEER_INVOICE: "ACTION_SEND_ENGINEER_INVOICE",
  ACTION_PAID_ENGINEER_INVOICE: "ACTION_PAID_ENGINEER_INVOICE",
  COMPANY_BRANCHES: "COMPANY_BRANCHES",
  CLIENT_BANK_ACCOUNTS: "CLIENT_BANK_ACCOUNTS",
  WORK_FORCE_ENGINEERS: "WORK_FORCE_ENGINEERS",
  WORK_FORCE_TALENT_POOL: "WORK_FORCE_TALENT_POOL",
  WORK_FORCE_COVERAGE_MAP: "WORK_FORCE_COVERAGE_MAP",
  ENGINEER_SKILLS_SET: "ENGINEER_SKILLS_SET",
  TASK_BOARD: "TASK_BOARD",
  FREELANCE_COMPANY_WORKERS: "FREELANCE_COMPANY_WORKERS",
  CUSTOMER_USERS: "CUSTOMER_USERS",
});

export const ModuleNames = {
  [ModuleType.WORK_ORDERS]: "Work Orders",
  [ModuleType.CLIENT_WORK_ORDERS]: "Work Orders",
  [ModuleType.COMPANIES]: "Companies",
  [ModuleType.ADMIN_NEW_USERS_MANAGEMENT]: "Admin Account Management",
  [ModuleType.USER_PERMISSIONS]: "User Permissions",
  [ModuleType.WORK_EXPERIENCE]: "Work Experience",
  [ModuleType.QUALIFICATIONS]: "Qualifications",
  [ModuleType.USERS]: "Users",
  [ModuleType.JOBS]: "Jobs",
  [ModuleType.MANAGE]: "Manage",
  [ModuleType.CLIENT_CUSTOMER]: "Client Customer",
  [ModuleType.CLIENT_PROJECT]: "Client Project",
  [ModuleType.INVOICE]: "Invoice",
  [ModuleType.CUSTOMER_INVOICE]: "Customer Invoice",
  [ModuleType.ENGINEER_INVOICE]: "Engineer Invoice",
  [ModuleType.COMPANY_BRANCHES]: "Company Branches",
  [ModuleType.CLIENT_BANK_ACCOUNTS]: "Bank Accounts",
  [ModuleType.WORK_FORCE_COVERAGE_MAP]: "Coverage Map",
};

export const WORK_ORDER_EVENT_LABELS = {
  WORK_ORDER_CREATED: "Work Order Created",
  WORK_ORDER_UPDATED: "Work Order Updated",
  WORK_ORDER_DELETED: "Work Order Deleted",
  WORK_ORDER_NOTE_UPDATED: "Note Updated",
  WORK_ORDER_PO_ADDED: "Purchase Order Added",
  WORK_ORDER_PO_UPDATED: "Purchase Order Updated",
  WORK_ORDER_PO_DELETED: "Purchase Order Deleted",
  WORK_ORDER_STATUS_UPDATED: "Status Updated",
  WORK_ORDER_CANCELLED_BY_CLIENT: "Cancelled by Client",
  WORK_ORDER_CANCELLED_BY_ENGINEER: "Cancelled by Engineer",

  STATUS_UPDATED: "Status Updated",

  ASSIGNED_ENGINEER: "Engineer Assigned",
  UNASSIGNED_ENGINEER: "Engineer Unassigned",

  OFFER_SENT: "Offer Sent",
  OFFER_UPDATED: "Counter Offer Updated",
  OFFER_ACCEPTED: "Offer Accepted",
  OFFER_REJECTED: "Offer Rejected",
  OFFER_WITHDRAWN: "Offer Withdrawn",
  OFFER_DECLINED: "Engineer Not Interested",
  OFFER_ASSIGN: "Engineer Assigned",
  OFFER_UNASSIGNED: "Engineer Unassigned",
  OFFER_ROUTED: "Work Order Routed",

  PAYMENT_UPDATED: "Payment Updated",
  PAYMENT_APPROVED: "Payment Approved",
  CUSTOMER_PAYMENT_UPDATED: "Customer Payment Updated",
  ENGINEER_PAYMENT_UPDATED: "Engineer Payment Updated",

  TIMELOG_ADDED: "Time Log Added",
  TIMELOG_UPDATED: "Time Log Updated",
  CUSTOMER_TIMESHEET_UPDATED: "Customer Timesheet Updated",
  ENGINEER_TIMESHEET_UPDATED: "Engineer Timesheet Updated",

  EXPENSE_ADDED: "Expense Added",
  EXPENSE_REMOVED: "Expense Removed",
  EXPENSE_APPROVED: "Expense Approved",

  COMMENT_ADDED: "Comment Added",
  FILE_UPLOADED: "File Uploaded",
  DELIVERABLE_UPLOADED: "Deliverable Uploaded",
  DELIVERABLE_UPLOADED_DELETED: "Deliverable Deleted",
  CUSTOMER_FILE_UPLOADED: "Customer File Uploaded",
  CUSTOMER_FILE_DELETED: "Customer File Deleted",

  ENGINEER_CHECKED_IN: "Engineer Checked-in",
  ENGINEER_CHECKED_OUT: "Engineer Checked-out",

  SIGN_OFF_SHEET_CREATED: "Sign-Off Sheet Created",
  SIGN_OFF_SHEET_UPDATED: "Sign-Off Sheet Updated",

  FEEDBACK_GIVEN_TO_ENGINEER: "Feedback Given to Engineer",
  FEEDBACK_GIVEN_TO_CLIENT: "Feedback Given to Client",

  OTHER: "Other Activity",
};

export const CanceledByType = Object.freeze({
  CLIENT: "CLIENT",
  FREELANCER: "FREELANCER",
});

export const CLIENT_CANCELLATION_REASONS = [
  { value: "CLIENT_REQUESTED", label: "Client Requested Cancellation" },
  { value: "SITE_ACCESS_UNAVAILABLE", label: "Site Access Unavailable" },
  { value: "PROJECT_POSTPONED", label: "Project Postponed" },
  { value: "PARTS_NOT_AVAILABLE", label: "Parts Not Available" },
  { value: "ISSUE_RESOLVED_REMOTELY", label: "Issue Resolved Remotely" },
  { value: "OTHER", label: "Other" },
];

export const TECH_CANCELLATION_REASONS = [
  { value: "MEDICAL_EMERGENCY", label: "Medical Emergency" },
  { value: "FAMILY_EMERGENCY", label: "Family Emergency" },
  { value: "BEREAVEMENT", label: "Bereavement" },
  { value: "CUSTOMER_RESCHEDULED", label: "Customer Rescheduled" },
  { value: "SITE_ACCESS_ISSUES", label: "Site Access Issues" },
  { value: "OTHER", label: "Other" },
];

// All unique reasons: client-specific first, then tech-specific, OTHER once at end
export const ALL_CANCELLATION_REASONS = [
  ...CLIENT_CANCELLATION_REASONS.filter((r) => r.value !== "OTHER"),
  ...TECH_CANCELLATION_REASONS,
];

// Derived labels map — single source of truth from the two arrays above
export const CANCELLATION_REASON_LABELS = Object.fromEntries(
  [...CLIENT_CANCELLATION_REASONS, ...TECH_CANCELLATION_REASONS].map((r) => [r.value, r.label]),
);

// Sets for auto-detecting which side a reason exclusively belongs to (OTHER excluded)
export const CLIENT_ONLY_CANCELLATION_REASONS = new Set(
  CLIENT_CANCELLATION_REASONS.filter((r) => r.value !== "OTHER").map((r) => r.value),
);

export const TECH_ONLY_CANCELLATION_REASONS = new Set(
  TECH_CANCELLATION_REASONS.filter((r) => r.value !== "OTHER").map((r) => r.value),
);

export const ProviderRowType = Object.freeze({
  FREELANCE_COMPANY: "freelanceCompany",
});
